// GisNetbeheerder.js: het blok "GIS netbeheerder" in het venster Bewerken,
// plus twee hulpjes die de onboarding ook gebruikt.
//
// Het GeoPackage van Fluvius wordt op de server ingelezen. De server bewaart
// alleen de lagen van het stroomnet, niet het bestand zelf. Zie gisnet.js in
// de backend. Zonder bestand toont dit blok alleen "Nog geen bestand." en een
// knop; de rest van de app merkt dan niets.

import React, { useEffect, useRef, useState } from 'react';
import { getGisnet, laadGisnet, verwijderGisnet } from '../api';

const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', line:'#D9D5CD', line2:'#C4BFB5',
  accent:'#1F5C6B', red:'#B4442F', redBg:'#FDF0EE',
};

// Kilometers: vanaf 10 km een heel getal, daaronder één decimaal.
function km(v) {
  const n = Number(v) || 0;
  return n >= 10 ? Math.round(n).toLocaleString('nl-BE') : (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
}

export function samenvattingTekst(sv) {
  if (!sv) return '';
  const ls = (Number(sv.km400) || 0) + (Number(sv.km230) || 0) + (Number(sv.kmOnbekend) || 0);
  // Het planbestand van Fluvius heeft geen verdeelkasten en geen
  // middenspanning; dat staat er dan met zoveel woorden bij (26 oktober 2026).
  if (sv.soort === 'plan') {
    return `${sv.cabinesFluvius} MS-cabines Fluvius · `
      + `${km(ls)} km laagspanning, waarvan ${km(sv.km400)} km op 400 V en ${km(sv.km230)} km op 230 V · `
      + 'Planbestand van Fluvius: zonder verdeelkasten, middenspanningskabels en MS-cabines van bedrijven';
  }
  return `${sv.cabinesFluvius} MS-cabines Fluvius · ${sv.verdeelkasten} verdeelkasten · `
    + `${km(ls)} km laagspanning, waarvan ${km(sv.km400)} km op 400 V en ${km(sv.km230)} km op 230 V · `
    + `${km(sv.kmMidden)} km middenspanning`;
}

// De regel over het planbestand van Fluvius, als dat is ingelezen.
export function planTekst(plan) {
  if (!plan) return '';
  return `${km(plan.kmVersterken)} km net te versterken · `
    + `${plan.transfoProbleem} MS-cabines met verwacht probleem aan de transformator · `
    + `${plan.zoekzones} zoekzones nieuwe cabine · ${plan.projecten} werken van Fluvius`;
}

export function peildatumTekst(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `Peildatum ${d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function teken(v, eenheid = '') {
  const n = Number(v) || 0;
  const t = Number.isInteger(n) ? String(Math.abs(n)) : String(Math.abs(n)).replace('.', ',');
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${t}${eenheid}`;
}

export function verschilTekst(vs) {
  if (!vs) return '';
  return `Sinds de vorige versie: 400 V-kabel ${teken(vs.km400, ' km')}, MS-cabines Fluvius ${teken(vs.cabinesFluvius)}.`;
}

// Is dit een GeoPackage? Fluvius levert het bestand soms zonder extensie.
// Daarom niet op de naam toetsen maar op de inhoud: een GeoPackage is een
// SQLite-bestand en begint met "SQLite format 3". De server toetst daarna of
// de lagen van het stroomnet erin zitten.
export async function isGeoPackage(bestand) {
  try {
    const kop = new Uint8Array(await bestand.slice(0, 16).arrayBuffer());
    return String.fromCharCode(...kop.slice(0, 15)) === 'SQLite format 3';
  } catch (e) {
    console.error('Bestand niet te lezen:', e.message);
    return false;
  }
}

// Het blok in het venster Bewerken.
export default function GisNetbeheerder({ gemeenteId, onGewijzigd }) {
  const [stand, setStand] = useState(null);       // antwoord van de server
  const [bezig, setBezig] = useState(true);
  const [fout, setFout] = useState('');
  const [vraag, setVraag] = useState(false);      // bevestiging van verwijderen
  const [verschil, setVerschil] = useState(null); // alleen direct na een update
  const kiezer = useRef(null);

  useEffect(() => {
    let geldig = true;
    setBezig(true); setFout('');
    getGisnet(gemeenteId)
      .then((r) => { if (geldig) setStand(r); })
      .catch((e) => { if (geldig) setFout(e.message); })
      .finally(() => { if (geldig) setBezig(false); });
    return () => { geldig = false; };
  }, [gemeenteId]);

  const kies = () => { setFout(''); kiezer.current?.click(); };

  const gekozen = async (e) => {
    const bestand = e.target.files?.[0];
    e.target.value = '';
    if (!bestand) return;
    if (!(await isGeoPackage(bestand))) {
      setFout('Kies een GeoPackage-bestand (.gpkg).');
      return;
    }
    const hadAl = !!stand?.aanwezig;
    setBezig(true); setFout(''); setVraag(false);
    try {
      const r = await laadGisnet(gemeenteId, bestand);
      setStand(r);
      setVerschil(hadAl ? r.verschil : null);
      onGewijzigd?.();
    } catch (err) {
      setFout(`Het bestand kon niet worden ingelezen: ${err.message}`);
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async () => {
    setBezig(true); setFout('');
    try {
      await verwijderGisnet(gemeenteId);
      setStand({ aanwezig: false }); setVerschil(null); setVraag(false);
      onGewijzigd?.();
    } catch (err) {
      setFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  const s = {
    blok:  { border:`1px solid ${N.line2}`, borderRadius:10, padding:'15px 17px' },
    kop:   { fontSize:14, fontWeight:700, color:N.ink, marginBottom:10 },
    leeg:  { border:`2px dashed ${N.line2}`, borderRadius:10, padding:'18px', textAlign:'center', color:N.ink3, fontSize:14 },
    naam:  { fontSize:14.5, fontWeight:700, color:N.ink },
    meta:  { fontSize:13, color:N.ink3, margin:'3px 0 8px' },
    tekst: { fontSize:14, color:N.ink2, lineHeight:1.55 },
    rij:   { display:'flex', gap:8, marginTop:12, flexWrap:'wrap' },
    knop:  (soort, uit) => ({
      background: soort === 'vol' ? (uit ? N.line2 : N.accent) : N.paper,
      color: soort === 'vol' ? '#fff' : soort === 'rood' ? N.red : N.accent,
      border: `1px solid ${soort === 'rood' ? N.red : uit ? N.line2 : N.accent}`,
      borderRadius:8, padding:'8px 15px', fontSize:13.5, fontWeight:600,
      cursor: uit ? 'not-allowed' : 'pointer', fontFamily:'inherit' }),
    vraag: { background:N.redBg, borderRadius:8, padding:'10px 12px', marginTop:12, fontSize:14, color:N.ink },
    vs:    { background:'#FFF7E0', borderRadius:8, padding:'8px 10px', marginTop:12, fontSize:13.5, color:N.ink2 },
    fout:  { fontSize:12.5, color:N.red, fontWeight:600, marginTop:10 },
  };

  return (
    <div style={s.blok}>
      <div style={s.kop}>GIS netbeheerder</div>
      <input ref={kiezer} type="file" style={{ display:'none' }} onChange={gekozen} />
      {bezig && !stand ? (
        <div style={s.tekst}>Bezig…</div>
      ) : stand?.aanwezig ? (
        <div>
          <div style={s.naam}>{stand.bestandsnaam}</div>
          <div style={s.meta}>{peildatumTekst(stand.peildatum)}</div>
          <div style={s.tekst}>{samenvattingTekst(stand.samenvatting)}</div>
          {/* Een detailbestand en een planbestand van Fluvius vullen elkaar
              aan (besluit HL 26 oktober 2026). Staat het plan in een eigen
              bestand naast het detailbestand, dan krijgt het een eigen regel. */}
          {stand.samenvatting?.plan && stand.samenvatting.soort !== 'plan' && (<>
            <div style={{ ...s.naam, marginTop:12 }}>{stand.samenvatting.plan.bestandsnaam}</div>
            <div style={s.meta}>{peildatumTekst(stand.samenvatting.plan.peildatum)}</div>
          </>)}
          {stand.samenvatting?.plan && <div style={{ ...s.tekst, marginTop:6 }}>{planTekst(stand.samenvatting.plan)}</div>}
          {verschil && <div style={s.vs}>{verschilTekst(verschil)}</div>}
          {!vraag ? (
            <div style={s.rij}>
              <button style={s.knop('rand', bezig)} disabled={bezig} onClick={kies}>{bezig ? 'Bezig…' : 'Update'}</button>
              <button style={s.knop('rood', bezig)} disabled={bezig} onClick={() => setVraag(true)}>Verwijderen</button>
            </div>
          ) : (
            <div style={s.vraag}>
              {stand.samenvatting?.plan && stand.samenvatting.soort !== 'plan'
                ? 'Beide bestanden echt verwijderen?' : 'Bestand echt verwijderen?'}
              <div style={s.rij}>
                <button style={s.knop('rood', bezig)} disabled={bezig} onClick={verwijder}>Ja</button>
                <button style={s.knop('rand', bezig)} disabled={bezig} onClick={() => setVraag(false)}>Nee</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={s.leeg}>
          Nog geen bestand.
          <div style={{ ...s.rij, justifyContent:'center' }}>
            <button style={s.knop('vol', bezig)} disabled={bezig} onClick={kies}>{bezig ? 'Bezig…' : 'Bestand kiezen'}</button>
          </div>
        </div>
      )}
      {fout && <div style={s.fout}>{fout}</div>}
    </div>
  );
}
